import styled from "@emotion/styled"
import { library } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faChevronRight as fasChevronRight,
} from "@fortawesome/free-solid-svg-icons"
import { get } from "lodash"
import React, { useRef, useState } from "react"
import Slider from "react-slick"
import "slick-carousel/slick/slick-theme.css"
import "slick-carousel/slick/slick.css"
import tw from "twin.macro"
import useWindowSize from "../hooks/useWindowSize"
import Picture from "./Picture"
import VideoEmbed from "./VideoEmbed"
import Link from "./Link"
import linkTransformer from "../utils/linkTransformer"

library.add(
  fasChevronRight,
)


const Billboards = ({ billboards }) => {
  const ref = useRef(null)
  const [currentIndex, setCurrentIndex] = useState(0)

  const slickNext = () => {
    ref.current.slickNext()
  }

  const slickPrev = () => {
    ref.current.slickPrev()
  }

  return (
    <>
      <SliderNav slickNext={slickNext} />
      <Slider
        arrows={false}
        autoplay={false}
        fade={true}
        ref={ref}
        speed={1000}
        style={{ height: "480px" }}
        beforeChange={(index, nextIndex) => {
          setCurrentIndex(nextIndex)
        }}
      >
        {billboards.map((billboard, index) => {
          const id = get(billboard, "system.id")
          return (
            <div key={id}>
              <Billboard
                billboard={billboard}
                slickNext={slickNext}
                slickPrev={slickPrev}
                play={index === currentIndex}
              />
            </div>
          )
        })}
      </Slider>
    </>
  )
}

export default Billboards

const StyledBillboard = styled.div`
  ${tw`relative flex justify-end items-end`}
  height: 480px;
  a {
    ${tw`hidden absolute inset-0 h-full w-full`}
  }
  .slick-current & a {
    ${tw`block`}
  }
`
const Image = styled(Picture)`
  ${tw`h-full block w-full absolute inset-0 object-cover object-center`}
`

const StyledVideoEmbed = styled(VideoEmbed)`
  ${tw`w-full h-full absolute inset-0`}
`

const Text = styled.div`
  ${tw`uppercase absolute right-0 bottom-0 z-10 leading-none text-right uppercase text-white text-3xl lg:text-6xl font-bold`}
`

const StyledSliderNav = styled.div`
  ${tw`absolute right-0 z-20 text-white`}
  top: 50%;
  transform: translate(0, -50%);
  svg {
    filter: drop-shadow(0 0 8px rgba(0, 0, 0, 0.8));
  }
`

const SliderNext = styled.button`
  ${tw`py-2 pr-2`}
`

const Line = styled.div`
  span {
    ${tw`px-4 py-2 inline-block`}
    ${(props) => props.index === 1 && tw`bg-gray`}
    ${(props) => props.index === 2 && tw`bg-teal`}
    ${(props) => props.index === 3 && tw`bg-blue`}
  }
`

const SliderNav = ({ slickNext }) => {
  return (
    <StyledSliderNav>
      <SliderNext onClick={slickNext} aria-label="Next">
        <FontAwesomeIcon icon={["fas", "chevron-right"]} size="2x" />
      </SliderNext>
    </StyledSliderNav>
  )
}

const Billboard = ({ billboard, play }) => {
  const backgroundMedia = get(
    billboard,
    "elements.background_media.value[0]"
  )
  const backgroundMediaType = get(backgroundMedia, "system.type")
  const text = get(billboard, "elements.text.value")

  let lineIndex = 0
  const size = useWindowSize()
  const hasMobile = get(backgroundMedia, "elements.mobile_brightcove_id.value")
  const transformedLink = linkTransformer(billboard)

  const billboardMarkup = () => (
    <>
      {text ? (
        <Text>
          {text.split("\n").map((line, index) => {
            lineIndex = lineIndex < 3 ? lineIndex + 1 : 1

            return (
              <Line index={lineIndex} key={index}>
                <span>{line}</span>
              </Line>
            )
          })}
        </Text>
      ) : null}
      {
        {
          image: (
            <BillBoardImage
              media={
                hasMobile
                  ? {
                      ...backgroundMedia,
                      ...{ elements: { brightcove_id: { value: hasMobile } } },
                    }
                  : backgroundMedia
              }
              cover={true}
            />
          ),
          video_embed:
            (hasMobile && size && size.width < 840) ||
            (hasMobile && size && size.width >= 1024 && size.width < 1140) ? (
              <BillBoardVideoEmbed
                media={
                  hasMobile
                    ? {
                        ...backgroundMedia,
                        ...{
                          elements: { brightcove_id: { value: hasMobile } },
                        },
                      }
                    : backgroundMedia
                }
                play={play}
              />
            ) : (
              <BillBoardVideoEmbed media={backgroundMedia} play={play} />
            ),
        }[backgroundMediaType]
      }
    </>
  )

  return (
    <StyledBillboard>
      {transformedLink && transformedLink.to ? (
        <Link {...transformedLink}>{billboardMarkup()}</Link>
      ) : (
        billboardMarkup()
      )}
    </StyledBillboard>
  )
}

const BillBoardImage = ({ media }) => {
  const image = get(media, "elements.image.value[0]")
  const imageAltText = get(media, "elements.alt_text.value")
  const focalPoint = get(media, "elements.focal_point.value")

  return (
    <Image
      image={image}
      cropWidth={1200}
      cropRatio={0.5625}
      cover={true}
      alt={imageAltText}
      focalPoint={focalPoint}
    />
  )
}

const BillBoardVideoEmbed = ({ media, play }) => {
  return (
    <StyledVideoEmbed
      video={media}
      playInModal={false}
      cover={true}
      play={play}
      muted={true}
      playsinline={true}
      loop
    />
  )
}
